import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

function IconPhone() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81a19.79 19.79 0 01-3.07-8.66A2 2 0 012 .18h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.9v2.02z"/>
    </svg>
  );
}

function IconLayers() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
      <polyline points="2 17 12 22 22 17"/>
      <polyline points="2 12 12 17 22 12"/>
    </svg>
  );
}

export default function ProductDetail() {
  const navigate = useNavigate();
  const { id }   = useParams();

  const [product, setProduct]   = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`/products/${id}`),
      axios.get('/features', { params: { product_id: id, limit: 200 } }),
    ])
      .then(([pRes, fRes]) => {
        setProduct(pRes.data);
        setFeatures(fRes.data.data || []);
      })
      .catch(() => setError('Product not found'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!window.confirm(
      `Delete product "${product.name}"?\n\nFeatures assigned to it will become unassigned.`
    )) return;
    setDeleting(true);
    try {
      await axios.delete(`/products/${id}`);
      navigate('/products', { replace: true });
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading product…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-body">
        <div className="error-msg">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/products')}>
          <IconArrowLeft /> Back to Products
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>{product.name}</h2>
        <p>{product.pms.length} product manager{product.pms.length !== 1 ? 's' : ''} · {features.length} feature{features.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="page-body">
        {/* Nav + actions */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, alignItems: 'center' }}>
          <button className="btn btn-ghost" style={{ paddingLeft: 0 }}
            onClick={() => navigate('/products')}>
            <IconArrowLeft /> All Products
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={() => navigate(`/products/${id}/edit`)}>
            <IconEdit /> Edit
          </button>
          <button className="btn btn-danger" disabled={deleting} onClick={handleDelete}>
            <IconTrash /> {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 24, alignItems: 'start' }}>
          {/* Left – PMs */}
          <div>
            <div className="card">
              <div style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: 16,
              }}>
                Product Managers
              </div>

              {product.pms.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
                  No PMs assigned.{' '}
                  <button className="btn btn-ghost" style={{ padding: '0 4px', fontSize: 13 }}
                    onClick={() => navigate(`/products/${id}/edit`)}>
                    Add one
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {product.pms.map(pm => (
                    <div key={pm.id} style={{
                      padding: '12px 14px',
                      background: 'var(--surface-hover)',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                        {pm.pm_name}
                      </div>
                      {pm.pm_email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 3 }}>
                          <IconMail />
                          <a href={`mailto:${pm.pm_email}`} style={{ color: 'var(--accent)' }}>
                            {pm.pm_email}
                          </a>
                        </div>
                      )}
                      {pm.pm_phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                          <IconPhone />
                          {pm.pm_phone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>CREATED</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {product.created_at ? new Date(product.created_at).toLocaleString() : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, marginBottom: 4 }}>LAST UPDATED</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {product.updated_at ? new Date(product.updated_at).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Right – features */}
          <div className="card">
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 16,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.4px', color: 'var(--text-muted)',
              }}>
                Features ({features.length})
              </div>
              <button
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                onClick={() => navigate('/features/new')}
              >
                <IconLayers /> Add Feature
              </button>
            </div>

            {features.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
                No features assigned to this product yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {features.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className="btn btn-ghost"
                    style={{
                      justifyContent: 'flex-start', textAlign: 'left',
                      padding: '10px 12px', borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                    }}
                    onClick={() => navigate(`/features/${f.id}`)}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--accent)', fontSize: 13 }}>
                        {f.feature_id}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {f.major_feature}
                        {f.sub_feature ? ` › ${f.sub_feature}` : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
